import { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Calculator, Dumbbell, Gauge, Scale, HeartPulse } from 'lucide-react';
import {
  estimate1RM,
  percentTable,
  rpeEstimate,
  wilksScore,
  bmi,
  round1,
} from '../lib/calculators';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.06 } },
};
const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

const toNum = (v: string): number => {
  const n = parseFloat(v.replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};
const fmt = (n: number): string => (n > 0 ? String(round1(n)) : '—');

// ── Campo numérico reutilizable ──────────────────────────────────────
function NumberField({
  label,
  value,
  onChange,
  placeholder,
  suffix,
  step,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  suffix?: string;
  step?: string;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="relative">
        <input
          type="number"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="input"
          placeholder={placeholder}
          min="0"
          step={step ?? 'any'}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-dark-500 pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </div>
  );
}

function ResultBig({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <div className="rounded-xl bg-dark-800/60 border border-dark-700/50 p-4 text-center">
      <p className="text-xs uppercase tracking-wide text-dark-500 mb-1">{label}</p>
      <p className="text-3xl font-display font-bold text-gradient">
        {value}
        {unit && value !== '—' && <span className="text-lg text-dark-400 ml-1">{unit}</span>}
      </p>
    </div>
  );
}

// ── 1RM ──────────────────────────────────────────────────────────────
function OneRMCalc() {
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const w = toNum(weight);
  const r = toNum(reps);
  const oneRM = estimate1RM(w, r);
  const table = useMemo(() => (oneRM > 0 ? percentTable(oneRM) : []), [oneRM]);

  return (
    <div className="space-y-5">
      <p className="text-sm text-dark-400">
        Estimá el máximo de una repetición a partir de una serie. Promedia las fórmulas de
        Epley y Brzycki.
      </p>
      <div className="grid grid-cols-2 gap-4">
        <NumberField label="Peso levantado" value={weight} onChange={setWeight} placeholder="100" suffix="kg" />
        <NumberField label="Repeticiones" value={reps} onChange={setReps} placeholder="5" />
      </div>

      <ResultBig label="1RM estimado" value={fmt(oneRM)} unit="kg" />

      {table.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-dark-700/50">
          <table className="w-full text-sm">
            <thead className="bg-dark-800/60 text-dark-400">
              <tr>
                <th className="text-left px-3 py-2 font-medium">%1RM</th>
                <th className="text-right px-3 py-2 font-medium">Carga</th>
                <th className="text-right px-3 py-2 font-medium">Reps aprox.</th>
              </tr>
            </thead>
            <tbody>
              {table.map((row) => (
                <tr key={row.percent} className="border-t border-dark-700/40">
                  <td className="px-3 py-1.5 text-dark-300">{row.percent}%</td>
                  <td className="px-3 py-1.5 text-right font-medium text-dark-100">
                    {round1(row.weight)} kg
                  </td>
                  <td className="px-3 py-1.5 text-right text-dark-400">{row.reps}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── RPE → %1RM ───────────────────────────────────────────────────────
const RPE_OPTIONS = [10, 9.5, 9, 8.5, 8, 7.5, 7, 6.5, 6];

function RpeCalc() {
  const [weight, setWeight] = useState('');
  const [reps, setReps] = useState('');
  const [rpe, setRpe] = useState('8');
  const res = rpeEstimate(toNum(weight), toNum(reps), toNum(rpe));

  return (
    <div className="space-y-5">
      <p className="text-sm text-dark-400">
        A partir de una serie con RPE (esfuerzo percibido), estimá el 1RM y a qué % del 1RM
        corresponde. RIR = 10 − RPE.
      </p>
      <div className="grid grid-cols-3 gap-4">
        <NumberField label="Peso" value={weight} onChange={setWeight} placeholder="80" suffix="kg" />
        <NumberField label="Reps" value={reps} onChange={setReps} placeholder="6" />
        <div>
          <label className="label">RPE</label>
          <select value={rpe} onChange={(e) => setRpe(e.target.value)} className="input">
            {RPE_OPTIONS.map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <ResultBig label="1RM estimado" value={fmt(res.oneRM)} unit="kg" />
        <ResultBig label="% del 1RM" value={res.percent > 0 ? String(Math.round(res.percent)) : '—'} unit="%" />
      </div>
      {res.repsToFailure > 0 && (
        <p className="text-xs text-dark-500 text-center">
          Equivale a ~{res.repsToFailure} reps al fallo.
        </p>
      )}
    </div>
  );
}

// ── Wilks ────────────────────────────────────────────────────────────
function WilksCalc() {
  const [sex, setSex] = useState<'male' | 'female'>('male');
  const [bodyweight, setBodyweight] = useState('');
  const [total, setTotal] = useState('');
  const score = wilksScore(toNum(total), toNum(bodyweight), sex);

  return (
    <div className="space-y-5">
      <p className="text-sm text-dark-400">
        Compara la fuerza relativa ajustando el total levantado por el peso corporal y el sexo
        (puntaje Wilks). Útil para comparar atletas de distinto peso.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="label">Sexo</label>
          <select
            value={sex}
            onChange={(e) => setSex(e.target.value as 'male' | 'female')}
            className="input"
          >
            <option value="male">Masculino</option>
            <option value="female">Femenino</option>
          </select>
        </div>
        <NumberField label="Peso corporal" value={bodyweight} onChange={setBodyweight} placeholder="80" suffix="kg" />
        <NumberField label="Total levantado" value={total} onChange={setTotal} placeholder="400" suffix="kg" />
      </div>
      <ResultBig label="Puntaje Wilks" value={score > 0 ? String(round1(score)) : '—'} />
    </div>
  );
}

// ── IMC ──────────────────────────────────────────────────────────────
function BmiCalc() {
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const res = bmi(toNum(weight), toNum(height));

  return (
    <div className="space-y-5">
      <p className="text-sm text-dark-400">Índice de masa corporal a partir del peso y la altura.</p>
      <div className="grid grid-cols-2 gap-4">
        <NumberField label="Peso" value={weight} onChange={setWeight} placeholder="72" suffix="kg" />
        <NumberField label="Altura" value={height} onChange={setHeight} placeholder="175" suffix="cm" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <ResultBig label="IMC" value={res.bmi > 0 ? String(round1(res.bmi)) : '—'} />
        <ResultBig label="Categoría" value={res.category || '—'} />
      </div>
      <p className="text-xs text-dark-500">
        El IMC es orientativo: no distingue masa muscular de grasa, así que en atletas puede
        sobreestimar.
      </p>
    </div>
  );
}

// ── Página ───────────────────────────────────────────────────────────
const CALCULATORS = [
  { id: 'rm', label: '1RM', icon: Dumbbell, render: () => <OneRMCalc /> },
  { id: 'rpe', label: 'RPE → %1RM', icon: Gauge, render: () => <RpeCalc /> },
  { id: 'wilks', label: 'Wilks', icon: Scale, render: () => <WilksCalc /> },
  { id: 'bmi', label: 'IMC', icon: HeartPulse, render: () => <BmiCalc /> },
];

export function Calculators() {
  const [active, setActive] = useState('rm');
  const current = CALCULATORS.find((c) => c.id === active) ?? CALCULATORS[0];

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible" className="space-y-6">
      <motion.div variants={itemVariants}>
        <h1 className="section-title flex items-center gap-3">
          <Calculator className="w-8 h-8 text-dark-100" />
          Calculadoras
        </h1>
        <p className="section-subtitle">Herramientas rápidas para programar y comparar cargas (en kg).</p>
      </motion.div>

      {/* Selector */}
      <motion.div variants={itemVariants} className="flex flex-wrap gap-2">
        {CALCULATORS.map((calc) => {
          const Icon = calc.icon;
          const isActive = calc.id === active;
          return (
            <button
              key={calc.id}
              onClick={() => setActive(calc.id)}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-colors ${
                isActive
                  ? 'bg-primary-500/10 border-primary-500/30 text-primary-300'
                  : 'border-dark-700 text-dark-300 hover:text-dark-100 hover:bg-dark-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {calc.label}
            </button>
          );
        })}
      </motion.div>

      {/* Calculadora activa */}
      <motion.div key={active} variants={itemVariants} className="card max-w-2xl">
        {current.render()}
      </motion.div>
    </motion.div>
  );
}
